import React, { useState, useEffect } from 'react';
import {
  AlertTriangle, TrendingUp, Users, Shield, Briefcase,
  Calendar, Building, ChevronRight, RefreshCw, Loader2,
  AlertCircle, CheckCircle, Clock, Target, Award,
  Wrench, FileText, DollarSign, BarChart3, PieChart,
  Activity, Zap, Bell, ArrowUpRight, ArrowDownRight,
  Trophy, XCircle, Info, Brain, Sparkles, Lightbulb
} from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell
} from 'recharts';
import { toast } from 'sonner';

const COLORS = ['#DC2626', '#F97316', '#EAB308', '#22C55E', '#3B82F6', '#8B5CF6'];

export default function OperationsControlCenter() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // AI Insights state
  const [insights, setInsights] = useState([]);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [showInsights, setShowInsights] = useState(true);

  useEffect(() => {
    const loadAll = async () => {
      try {
        // Load dashboard data
        const dashData = await apiRequest('/occ/dashboard');
        if (dashData) setDashboard(dashData);
        
        // Load insights
        const insightsData = await apiRequest('/occ/insights/');
        if (insightsData?.insights) setInsights(insightsData.insights);
      } catch (error) {
        console.error('Error loading OCC data:', error);
      } finally {
        setLoading(false);
        setLoadingInsights(false);
      }
    };
    
    loadAll();
  }, []);

  const fetchDashboard = async () => {
    setRefreshing(true);
    try {
      const data = await apiRequest('/occ/dashboard');
      if (data) {
        setDashboard(data);
      }
    } catch (error) {
      console.error('Dashboard error:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setRefreshing(false);
    }
  };

  const fetchInsights = async () => {
    setLoadingInsights(true);
    try {
      const data = await apiRequest('/occ/insights/');
      setInsights(data.insights || []);
    } catch (error) {
      console.error('Failed to load AI insights:', error);
    } finally {
      setLoadingInsights(false);
    }
  };

  const refreshInsights = async () => {
    setLoadingInsights(true);
    try {
      await apiRequest('/occ/insights/refresh', { method: 'POST' });
      await fetchInsights();
      toast.success('AI insights refreshed');
    } catch (error) {
      toast.error('Failed to refresh insights');
    } finally {
      setLoadingInsights(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'warning': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'info': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <XCircle className="h-5 w-5 text-red-600" />;
      case 'warning': return <AlertTriangle className="h-5 w-5 text-orange-600" />;
      case 'info': return <Info className="h-5 w-5 text-blue-600" />;
      default: return <AlertCircle className="h-5 w-5 text-slate-600" />;
    }
  };

  // Provide default empty values if dashboard is still loading
  const critical_alerts = dashboard?.critical_alerts || [];
  const key_metrics = dashboard?.key_metrics || {};
  const hr_summary = dashboard?.hr_summary || {};
  const compliance_summary = dashboard?.compliance_summary || {};
  const operations_summary = dashboard?.operations_summary || {};
  const projects_summary = dashboard?.projects_summary || {};
  const scheduling_summary = dashboard?.scheduling_summary || {};
  const trends = dashboard?.trends || { dates: [], tasks_completed: [], incidents: [], labor_cost_pct: [] };
  const store_rankings = dashboard?.store_rankings || [];

  const criticalCount = critical_alerts?.filter(a => a.severity === 'critical').length || 0;
  const warningCount = critical_alerts?.filter(a => a.severity === 'warning').length || 0;

  return (
    <div className="space-y-6" data-testid="occ-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Inter' }}>
            Operations Control Center
          </h1>
          <p className="text-slate-600 mt-1">
            Unified view of HR, Compliance, Operations, and Projects
          </p>
        </div>
        <div className="flex items-center gap-3">
          {criticalCount > 0 && (
            <Badge variant="destructive" className="text-sm px-3 py-1 animate-pulse">
              <AlertCircle className="h-4 w-4 mr-1" />
              {criticalCount} Critical
            </Badge>
          )}
          {warningCount > 0 && (
            <Badge className="bg-orange-100 text-orange-700 text-sm px-3 py-1">
              <AlertTriangle className="h-4 w-4 mr-1" />
              {warningCount} Warnings
            </Badge>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchDashboard(true)}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Critical Alerts Banner */}
      {critical_alerts && critical_alerts.length > 0 && (
        <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Bell className="h-5 w-5 text-red-600" />
            <h3 className="font-semibold text-red-800">Alerts Requiring Attention</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {critical_alerts.slice(0, 6).map((alert) => (
              <div
                key={alert.id}
                className={`flex items-start gap-3 p-3 rounded-lg border ${getSeverityColor(alert.severity)} cursor-pointer hover:shadow-md transition-shadow`}
                onClick={() => window.location.href = alert.action_url}
              >
                {getSeverityIcon(alert.severity)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{alert.title}</span>
                    <Badge variant="outline" className="ml-2 text-xs">{alert.category}</Badge>
                  </div>
                  <p className="text-xs mt-1 opacity-80">{alert.message}</p>
                </div>
                <ChevronRight className="h-4 w-4 opacity-50" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI-Powered Insights Panel */}
      <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                <Brain className="h-5 w-5 text-purple-600" />
              </div>
              <span>AI-Powered Insights</span>
              <Badge className="bg-purple-100 text-purple-700 text-xs">
                <Sparkles className="h-3 w-3 mr-1" />
                GPT
              </Badge>
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowInsights(!showInsights)}
                className="text-purple-600"
              >
                {showInsights ? 'Hide' : 'Show'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={refreshInsights}
                disabled={loadingInsights}
                className="border-purple-200 text-purple-700 hover:bg-purple-100"
              >
                {loadingInsights ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        {showInsights && (
          <CardContent>
            {loadingInsights && insights.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-purple-600 mr-2" />
                <span className="text-purple-600">Analyzing your data...</span>
              </div>
            ) : insights.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {insights.slice(0, 6).map((insight) => (
                  <InsightCard key={insight.id} insight={insight} />
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-slate-500">
                <Lightbulb className="h-8 w-8 mx-auto mb-2 text-slate-400" />
                <p>No insights available. Click refresh to generate.</p>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        <MetricCard
          label="Total Staff"
          value={key_metrics?.total_staff || 0}
          icon={Users}
          color="bg-blue-500"
        />
        <MetricCard
          label="Active Projects"
          value={key_metrics?.active_projects || 0}
          icon={Briefcase}
          color="bg-purple-500"
        />
        <MetricCard
          label="Compliance"
          value={`${key_metrics?.compliance_score || 0}%`}
          icon={Shield}
          color="bg-green-500"
          trend={key_metrics?.compliance_score > 80 ? 'up' : 'down'}
        />
        <MetricCard
          label="Tasks Done"
          value={key_metrics?.tasks_completed_week || 0}
          sublabel="This Week"
          icon={CheckCircle}
          color="bg-teal-500"
        />
        <MetricCard
          label="Avg KPI"
          value={key_metrics?.avg_kpi_score || 0}
          sublabel="Out of 5"
          icon={Target}
          color="bg-orange-500"
        />
        <MetricCard
          label="Stores"
          value={key_metrics?.total_stores || 0}
          icon={Building}
          color="bg-indigo-500"
        />
        <MetricCard
          label="Incidents"
          value={key_metrics?.incidents_month || 0}
          sublabel="This Month"
          icon={AlertTriangle}
          color="bg-red-500"
          trend={key_metrics?.incidents_month > 5 ? 'down' : 'up'}
        />
        <MetricCard
          label="Policy"
          value={`${key_metrics?.policy_compliance || 0}%`}
          sublabel="Completion"
          icon={FileText}
          color="bg-cyan-500"
        />
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="overview" className="data-[state=active]:bg-white">
            <BarChart3 className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="hr" className="data-[state=active]:bg-white">
            <Users className="h-4 w-4 mr-2" />
            HR
          </TabsTrigger>
          <TabsTrigger value="compliance" className="data-[state=active]:bg-white">
            <Shield className="h-4 w-4 mr-2" />
            Compliance
          </TabsTrigger>
          <TabsTrigger value="operations" className="data-[state=active]:bg-white">
            <Wrench className="h-4 w-4 mr-2" />
            Operations
          </TabsTrigger>
          <TabsTrigger value="projects" className="data-[state=active]:bg-white">
            <Briefcase className="h-4 w-4 mr-2" />
            Projects
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Tasks Completed Trend */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium flex items-center gap-2">
                  <Activity className="h-5 w-5 text-green-600" />
                  Tasks Completed (30 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trends?.dates?.map((date, i) => ({
                      date,
                      tasks: trends?.tasks_completed?.[i] || 0
                    })) || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={6} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Area type="monotone" dataKey="tasks" stroke="#22C55E" fill="#22C55E" fillOpacity={0.2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Labor Cost Trend */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-orange-600" />
                  Labor Cost % (30 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trends?.dates?.map((date, i) => ({
                      date,
                      cost: trends?.labor_cost_pct?.[i] || 0,
                      target: 25
                    })) || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={6} />
                      <YAxis tick={{ fontSize: 10 }} domain={[0, 40]} />
                      <Tooltip />
                      <Line type="monotone" dataKey="target" stroke="#94A3B8" strokeDasharray="5 5" dot={false} />
                      <Line type="monotone" dataKey="cost" stroke="#F97316" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Store Rankings */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-yellow-600" />
                  Store Rankings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {store_rankings?.slice(0, 5).map((store, idx) => (
                    <div key={store.store} className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                        idx === 1 ? 'bg-slate-100 text-slate-700' :
                        idx === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-slate-50 text-slate-500'
                      }`}>
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm">{store.store}</span>
                          <span className="text-sm font-semibold text-slate-700">{store.overall_score?.toFixed(1)}%</span>
                        </div>
                        <Progress value={store.overall_score || 0} className="h-2" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Incidents Trend */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  Incidents (30 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={trends?.dates?.map((date, i) => ({
                      date,
                      incidents: trends?.incidents?.[i] || 0
                    })) || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={6} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Bar dataKey="incidents" fill="#EF4444" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* HR Tab */}
        <TabsContent value="hr" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Onboarding Pipeline"
              value={hr_summary?.onboarding_pending + hr_summary?.onboarding_in_progress || 0}
              details={[
                { label: 'Pending', value: hr_summary?.onboarding_pending || 0 },
                { label: 'In Progress', value: hr_summary?.onboarding_in_progress || 0 }
              ]}
              icon={Users}
              color="blue"
            />
            <StatCard
              title="Reviews Due"
              value={hr_summary?.reviews_due || 0}
              sublabel="Within 14 days"
              icon={FileText}
              color="orange"
            />
            <StatCard
              title="Leave This Week"
              value={hr_summary?.leave_this_week || 0}
              sublabel="Approved"
              icon={Calendar}
              color="purple"
            />
            <StatCard
              title="Training Completion"
              value={`${hr_summary?.training_completion_rate || 0}%`}
              icon={Award}
              color="green"
            />
          </div>

          {/* Staff by Role */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Staff Distribution by Role</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={Object.entries(hr_summary?.staff_by_role || {}).map(([name, value]) => ({
                        name: name.replace('_', ' ').toUpperCase(),
                        value
                      }))}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {Object.entries(hr_summary?.staff_by_role || {}).map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Policy Compliance"
              value={`${compliance_summary?.policy_completion_rate || 0}%`}
              details={[
                { label: 'Completed', value: compliance_summary?.policies_completed || 0 },
                { label: 'Pending', value: compliance_summary?.policies_pending || 0 }
              ]}
              icon={FileText}
              color="green"
            />
            <StatCard
              title="Incidents This Month"
              value={compliance_summary?.incidents_total || 0}
              icon={AlertTriangle}
              color="red"
            />
            <StatCard
              title="Corrective Actions"
              value={compliance_summary?.corrective_actions?.open + compliance_summary?.corrective_actions?.in_progress || 0}
              sublabel="Open/In Progress"
              icon={Wrench}
              color="orange"
            />
            <StatCard
              title="Temp Violations"
              value={compliance_summary?.temp_violations || 0}
              sublabel="This Week"
              icon={Activity}
              color="cyan"
            />
          </div>

          {/* Store Compliance Scores */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Store Compliance Scores</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={compliance_summary?.store_scores || []}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} />
                    <YAxis dataKey="store" type="category" tick={{ fontSize: 10 }} width={80} />
                    <Tooltip />
                    <Bar dataKey="overall_score" fill="#22C55E" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Operations Tab */}
        <TabsContent value="operations" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Audits This Month"
              value={operations_summary?.audits_month || 0}
              sublabel={`Avg Score: ${operations_summary?.avg_audit_score || 0}%`}
              icon={Shield}
              color="blue"
            />
            <StatCard
              title="Equipment Status"
              value={operations_summary?.equipment?.operational || 0}
              details={[
                { label: 'Under Repair', value: operations_summary?.equipment?.under_repair || 0 },
                { label: 'Overdue', value: operations_summary?.equipment?.overdue_maintenance || 0, highlight: true }
              ]}
              icon={Wrench}
              color="green"
            />
            <StatCard
              title="Checklists Today"
              value={operations_summary?.checklists_today?.opening_complete + operations_summary?.checklists_today?.closing_complete || 0}
              details={[
                { label: 'Opening', value: operations_summary?.checklists_today?.opening_complete || 0 },
                { label: 'Closing', value: operations_summary?.checklists_today?.closing_complete || 0 }
              ]}
              icon={CheckCircle}
              color="teal"
            />
            <StatCard
              title="Daily Reports"
              value={operations_summary?.daily_reports_submitted || 0}
              sublabel="Submitted Today"
              icon={FileText}
              color="purple"
            />
          </div>

          {/* Scheduling Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Scheduling Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className={`text-3xl font-bold ${
                    scheduling_summary?.avg_labor_cost_pct > 30 ? 'text-red-600' :
                    scheduling_summary?.avg_labor_cost_pct > 25 ? 'text-orange-600' : 'text-green-600'
                  }`}>
                    {scheduling_summary?.avg_labor_cost_pct || 0}%
                  </div>
                  <p className="text-sm text-slate-500 mt-1">Avg Labor Cost</p>
                  <p className="text-xs text-slate-400">Target: {scheduling_summary?.labor_target}%</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">
                    {scheduling_summary?.shifts_scheduled_week || 0}
                  </div>
                  <p className="text-sm text-slate-500 mt-1">Shifts This Week</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-600">
                    {scheduling_summary?.swaps_pending || 0}
                  </div>
                  <p className="text-sm text-slate-500 mt-1">Swap Requests</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600">
                    {scheduling_summary?.overtime_hours_week || 0}
                  </div>
                  <p className="text-sm text-slate-500 mt-1">Overtime Hours</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Projects Tab */}
        <TabsContent value="projects" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Active Projects"
              value={projects_summary?.by_status?.active || 0}
              icon={Briefcase}
              color="blue"
            />
            <StatCard
              title="Task Completion"
              value={`${projects_summary?.tasks?.completion_rate || 0}%`}
              details={[
                { label: 'Completed', value: projects_summary?.tasks?.completed || 0 },
                { label: 'Total', value: projects_summary?.tasks?.total || 0 }
              ]}
              icon={CheckCircle}
              color="green"
            />
            <StatCard
              title="Overdue Tasks"
              value={projects_summary?.tasks?.overdue || 0}
              icon={Clock}
              color="red"
            />
            <StatCard
              title="Completed Projects"
              value={projects_summary?.by_status?.completed || 0}
              icon={Trophy}
              color="yellow"
            />
          </div>

          {/* Top Projects */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Top Active Projects</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {projects_summary?.top_projects?.map((project) => (
                  <div key={project.id} className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm">{project.name}</span>
                        <span className="text-sm text-slate-500">{project.progress}%</span>
                      </div>
                      <Progress value={project.progress} className="h-2" />
                    </div>
                    {project.due_date && (
                      <Badge variant="outline" className="text-xs">
                        Due: {new Date(project.due_date).toLocaleDateString()}
                      </Badge>
                    )}
                  </div>
                ))}
                {(!projects_summary?.top_projects || projects_summary.top_projects.length === 0) && (
                  <p className="text-slate-500 text-center py-4">No active projects</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Metric Card Component
function MetricCard({ label, value, sublabel, icon: Icon, color, trend }) {
  return (
    <Card className="relative overflow-hidden">
      <CardContent className="p-4">
        <div className={`absolute top-0 right-0 w-16 h-16 ${color} opacity-10 rounded-bl-full`} />
        <div className={`w-8 h-8 rounded-lg ${color} bg-opacity-20 flex items-center justify-center mb-2`}>
          <Icon className={`h-4 w-4 ${color.replace('bg-', 'text-')}`} />
        </div>
        <div className="flex items-end gap-1">
          <span className="text-2xl font-bold text-slate-900">{value}</span>
          {trend && (
            trend === 'up' 
              ? <ArrowUpRight className="h-4 w-4 text-green-500 mb-1" />
              : <ArrowDownRight className="h-4 w-4 text-red-500 mb-1" />
          )}
        </div>
        <p className="text-xs text-slate-500 mt-1">{label}</p>
        {sublabel && <p className="text-xs text-slate-400">{sublabel}</p>}
      </CardContent>
    </Card>
  );
}

// Stat Card Component
function StatCard({ title, value, sublabel, details, icon: Icon, color }) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    orange: 'bg-orange-100 text-orange-600',
    red: 'bg-red-100 text-red-600',
    purple: 'bg-purple-100 text-purple-600',
    teal: 'bg-teal-100 text-teal-600',
    cyan: 'bg-cyan-100 text-cyan-600',
    yellow: 'bg-yellow-100 text-yellow-600'
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
        <div className="mt-3">
          <div className="text-2xl font-bold text-slate-900">{value}</div>
          <p className="text-sm font-medium text-slate-700 mt-1">{title}</p>
          {sublabel && <p className="text-xs text-slate-500">{sublabel}</p>}
          {details && (
            <div className="mt-2 space-y-1">
              {details.map((d, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">{d.label}</span>
                  <span className={d.highlight ? 'text-red-600 font-medium' : 'text-slate-700'}>{d.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// AI Insight Card Component
function InsightCard({ insight }) {
  const priorityStyles = {
    critical: 'border-l-4 border-l-red-500 bg-red-50',
    high: 'border-l-4 border-l-orange-500 bg-orange-50',
    medium: 'border-l-4 border-l-yellow-500 bg-yellow-50',
    low: 'border-l-4 border-l-blue-500 bg-blue-50'
  };

  const priorityBadgeStyles = {
    critical: 'bg-red-100 text-red-700',
    high: 'bg-orange-100 text-orange-700',
    medium: 'bg-yellow-100 text-yellow-700',
    low: 'bg-blue-100 text-blue-700'
  };

  const categoryIcons = {
    compliance: Shield,
    hr: Users,
    operations: Wrench,
    projects: Briefcase,
    finance: DollarSign
  };

  const CategoryIcon = categoryIcons[insight.category] || Lightbulb;

  return (
    <div className={`rounded-lg p-4 ${priorityStyles[insight.priority] || 'bg-slate-50'} hover:shadow-md transition-shadow`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <CategoryIcon className="h-4 w-4 text-slate-600" />
          <Badge className={`text-xs ${priorityBadgeStyles[insight.priority]}`}>
            {insight.priority?.toUpperCase()}
          </Badge>
        </div>
        {insight.metric && (
          <span className="text-xs font-medium text-slate-500">{insight.metric}</span>
        )}
      </div>
      <h4 className="font-semibold text-sm text-slate-900 mb-1">{insight.title}</h4>
      <p className="text-xs text-slate-600 mb-2 line-clamp-2">{insight.recommendation}</p>
      {insight.impact && (
        <p className="text-xs text-slate-500 italic">
          <AlertTriangle className="h-3 w-3 inline mr-1" />
          {insight.impact}
        </p>
      )}
    </div>
  );
}
